import { definitions } from '@luxuryescapes/contract-trip'
import { Moment } from 'moment'

import {
  GetTripsResponse,
  GetTripResponse,
  GetTripMetadataResponse,
  RefreshTripResponse,
  DeleteTripResponse,
  UpdateTripRequest,
  UpdateTripResponse,
  CreateTripRequest,
  CreateTripResponse,
  GetTripSummaryResponse,
  GetTripCollaboratorsResponse,
  SendInviteResponse,
  SendInviteRequest,
  AcceptInviteResponse,
  EditCollaboratorResponse,
  EditCollaboratorRequest,
  RemoveCollaboratorResponse,
  DeleteInviteResponse,
  GetCuratedTripResponse,
  GetCuratedTripSummaryResponse,
  PublishTripRequest,
  PublishTripResponse,
  UnpublishTripResponse,
  UnpublishTripRequest,
  SubmitForApprovalResponse,
  SubmitForApprovalRequest,
  GetCuratedTripMetadataResponse,
  CopyTripTemplateResponse,
  CopyTripTemplateRequest,
  GetTripCuratorResponse,
  UpdateReviewResponse,
  UpdateReviewRequest,
  EditInviteResponse,
  EditInviteRequest,
  RecordTripViewResponse,
  GetAIAccommodationRequest,
  GetAIAccommodationResponse,
  GetAIDestinationsResponse,
  GetAIDestinationsRequest,
  GetAINotesRequest,
  GetAINotesResponse,
  GetAIAttractionsRequest,
  GetAIAttractionsResponse,
  GetAIDiningRequest,
  GetAIDiningResponse,
  GetLowStockTripItemsResponse,
} from './types'
import { baseTemplatesUri, baseUri, getOptions } from './utils'

import request from 'api/requestUtils'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import { SYDNEY_PLACE_INFO } from 'tripPlanner/config'
import { AITripSchema } from 'tripPlanner/types/common'

export function getTrip(
  tripId: string,
  accessToken?: string,
): Promise<definitions['fullTrip']> {
  const uri = `${baseUri}/${tripId}`
  return request
    .get<GetTripResponse>(uri, getOptions(accessToken))
    .then((response) => response.result)
}

export function getTripMetadata(
  tripId: string,
  accessToken?: string,
): Promise<definitions['tripMetadata']> {
  const uri = `${baseUri}/${tripId}/metadata`
  return request
    .get<GetTripMetadataResponse>(uri, getOptions(accessToken))
    .then((response) => response.result)
}

export function getTripSummary(
  tripId: string,
  accessToken?: string,
): Promise<definitions['tripSummary']> {
  const uri = `${baseUri}/${tripId}/summary`
  return request
    .get<GetTripSummaryResponse>(uri, getOptions(accessToken))
    .then((response) => response.result)
}

export function refreshTripOrders(
  tripId: string,
): Promise<definitions['refreshedTrip']> {
  const uri = `${baseUri}/${tripId}/refresh`
  return request
    .post<RefreshTripResponse, undefined>(uri, undefined, getOptions())
    .then((response) => response.result)
}

export function recordTripView(tripId: string): Promise<null> {
  const uri = `${baseUri}/${tripId}/record-view`
  return request
    .post<RecordTripViewResponse, undefined>(uri, undefined, getOptions())
    .then((response) => response.result)
}

export function getTrips(
  accessToken?: string,
): Promise<definitions['basicTrip'][]> {
  return request
    .get<GetTripsResponse>(baseUri, getOptions(accessToken))
    .then((response) => response.result)
}

export function getTripCollaborators(
  tripId: string,
  accessToken?: string,
): Promise<definitions['tripCollaborators']> {
  const uri = `${baseUri}/${tripId}/collaborators`
  return request
    .get<GetTripCollaboratorsResponse>(uri, getOptions(accessToken))
    .then((response) => response.result)
}

export function sendTripInvite(
  {
    tripId,
    email,
    role,
  }: {
    tripId: string
    email: string
    role: SendInviteRequest['role']
  },
  accessToken?: string,
): Promise<null> {
  const uri = `${baseUri}/${tripId}/invitation`
  return request
    .post<SendInviteResponse, SendInviteRequest>(
      uri,
      { email, role },
      getOptions(accessToken),
    )
    .then((response) => response.result)
}

export function acceptTripInvite(
  {
    tripId,
    inviteId,
  }: {
    tripId: string
    inviteId: string
  },
  accessToken?: string,
): Promise<definitions['fullTrip']> {
  const uri = `${baseUri}/${tripId}/invitations/${inviteId}/accept`
  return request
    .post<AcceptInviteResponse, undefined>(
      uri,
      undefined,
      getOptions(accessToken),
    )
    .then((response) => response.result)
}

export function deleteTripInvite(
  {
    tripId,
    invitationId,
  }: {
    tripId: string
    invitationId: string
  },
  accessToken?: string,
): Promise<null> {
  const uri = `${baseUri}/${tripId}/invitations/${invitationId}`
  return request
    .delete<DeleteInviteResponse>(uri, getOptions(accessToken))
    .then((response) => response.result)
}

export function removeCollaborator(
  {
    tripId,
    userId,
  }: {
    tripId: string
    userId: string
  },
  accessToken?: string,
): Promise<null> {
  const uri = `${baseUri}/${tripId}/collaborators/${userId}`
  return request
    .delete<RemoveCollaboratorResponse>(uri, getOptions(accessToken))
    .then((response) => response.result)
}

export function editCollaborator(
  {
    tripId,
    userId,
    role,
  }: {
    tripId: string
    userId: string
    role: SendInviteRequest['role']
  },
  accessToken?: string,
): Promise<null> {
  const uri = `${baseUri}/${tripId}/collaborators/${userId}`
  return request
    .patch<EditCollaboratorResponse, EditCollaboratorRequest>(
      uri,
      { role },
      getOptions(accessToken),
    )
    .then((response) => response.result)
}

export function editTripInvite(
  {
    tripId,
    invitationId,
    role,
  }: {
    tripId: string
    invitationId: string
    role: SendInviteRequest['role']
  },
  accessToken?: string,
): Promise<null> {
  const uri = `${baseUri}/${tripId}/invitations/${invitationId}`
  return request
    .patch<EditInviteResponse, EditInviteRequest>(
      uri,
      { role },
      getOptions(accessToken),
    )
    .then((response) => response.result)
}

export function deleteTrip(id: string, accessToken?: string): Promise<null> {
  const uri = `${baseUri}/${id}`
  return request
    .delete<DeleteTripResponse>(uri, getOptions(accessToken))
    .then((response) => response.result)
}

interface CreateArgs {
  name: string
  startDate?: Moment
  endDate?: Moment
  imageId?: string
}

export function createTrip(
  { name, startDate, endDate, imageId }: CreateArgs,
  accessToken?: string,
): Promise<definitions['fullTrip']> {
  return request
    .post<CreateTripResponse, CreateTripRequest>(
      baseUri,
      {
        name,
        originPlaceId: SYDNEY_PLACE_INFO.id, // TODO: remove this param from the API
        startDate: startDate?.format(ISO_DATE_FORMAT),
        endDate: endDate?.format(ISO_DATE_FORMAT),
        imageId,
      },
      getOptions(accessToken),
    )
    .then((response) => response.result)
}

interface UpdateArgs {
  tripId: string
  name?: string
  startDate?: Moment
  endDate?: Moment
  imageId?: string
  originPlaceId?: string
}

export function updateTrip(
  { tripId, name, startDate, endDate, imageId, originPlaceId }: UpdateArgs,
  accessToken?: string,
): Promise<definitions['fullTrip']> {
  const uri = `${baseUri}/${tripId}`

  return request
    .put<UpdateTripResponse, UpdateTripRequest>(
      uri,
      {
        name,
        startDate: startDate?.format(ISO_DATE_FORMAT) || undefined,
        endDate: endDate?.format(ISO_DATE_FORMAT) || undefined,
        imageId,
        originPlaceId,
      },
      getOptions(accessToken),
    )
    .then((response) => response.result)
}

export function getCuratedTrip(
  tripId: string,
  accessToken?: string,
): Promise<definitions['fullTrip']> {
  const uri = `${baseUri}/templates/${tripId}`
  return request
    .get<GetCuratedTripResponse>(uri, getOptions(accessToken))
    .then((response) => response.result)
}

export function getCuratedTripSummary(
  tripId: string,
  accessToken?: string,
): Promise<definitions['tripSummary']> {
  const uri = `${baseUri}/templates/${tripId}/summary`
  return request
    .get<GetCuratedTripSummaryResponse>(uri, getOptions(accessToken))
    .then((response) => response.result)
}

export function getCuratedTripMetadata(
  tripId: string,
  accessToken?: string,
): Promise<definitions['tripMetadata']> {
  const uri = `${baseUri}/templates/${tripId}/metadata`
  return request
    .get<GetCuratedTripMetadataResponse>(uri, getOptions(accessToken))
    .then((response) => response.result)
}

export function publishTripTemplate(
  {
    tripId,
  }: {
    tripId: string
  },
  accessToken?: string,
): Promise<null> {
  const uri = `${baseTemplatesUri}/${tripId}/publish`

  return request
    .put<PublishTripResponse, PublishTripRequest>(
      uri,
      { tripId },
      getOptions(accessToken),
    )
    .then((response) => response.result)
}

export function unpublishTripTemplate(
  {
    tripId,
  }: {
    tripId: string
  },
  accessToken?: string,
): Promise<null> {
  const uri = `${baseTemplatesUri}/${tripId}/unpublish`

  return request
    .put<UnpublishTripResponse, UnpublishTripRequest>(
      uri,
      { tripId },
      getOptions(accessToken),
    )
    .then((response) => response.result)
}

export function fetchUser(
  {
    email,
  }: {
    email: string
  },
  accessToken?: string,
): Promise<null> {
  const uri = `/api/users/${email}`
  return request
    .get<App.ApiResponse<null>>(uri, getOptions(accessToken))
    .then((response) => response.result)
}

export function submitTripForApproval(
  {
    tripId,
    userId,
    name,
    position,
    description,
    imageData,
  }: {
    tripId: string
    userId: string
    name: string
    position?: string
    imageData?: string
    description?: string
  },
  accessToken?: string,
): Promise<null> {
  const uri = `${baseTemplatesUri}/${tripId}/review`
  return request
    .post<SubmitForApprovalResponse, SubmitForApprovalRequest>(
      uri,
      { reviewerId: userId, name, position, description, imageData },
      getOptions(accessToken),
    )
    .then((response) => response.result)
}

export function updateReviewData(
  {
    tripId,
    name,
    position,
    description,
    imageData,
  }: {
    tripId: string
    name?: string
    position?: string
    description?: string
    imageData?: string
  },
  accessToken?: string,
): Promise<null> {
  const uri = `${baseTemplatesUri}/${tripId}/updateReview`
  return request
    .put<UpdateReviewResponse, UpdateReviewRequest>(
      uri,
      { name, position, description, imageData },
      getOptions(accessToken),
    )
    .then((response) => response.result)
}

interface CopyTemplateArgs {
  templateId: string
  name: string
  regionCode: string
  tripItemIds?: string[]
}

export function copyTripTemplate(
  { name, regionCode, templateId, tripItemIds }: CopyTemplateArgs,
  accessToken?: string,
): Promise<definitions['fullTrip']> {
  const uri = `${baseTemplatesUri}/${templateId}/copy`
  return request
    .post<CopyTripTemplateResponse, CopyTripTemplateRequest>(
      uri,
      {
        name,
        regionCode,
        tripItemIds,
      },
      getOptions(accessToken),
    )
    .then((response) => response.result)
}

interface SortArgs {
  tripId: string
  tripItemIds: string[]
}

export function sortTrip(
  { tripId, tripItemIds }: SortArgs,
  accessToken?: string,
): Promise<string[]> {
  const uri = `${baseUri}/${tripId}/sort`
  return request
    .post<App.ApiResponse<string[]>, string[]>(
      uri,
      tripItemIds,
      getOptions(accessToken),
    )
    .then((response) => response.result)
}

export function fetchCurator(
  {
    tripId,
  }: {
    tripId: string
  },
  accessToken?: string,
): Promise<definitions['tripCurator'] | undefined> {
  const uri = `${baseUri}/${tripId}/curator`
  return request
    .get<GetTripCuratorResponse>(uri, getOptions(accessToken))
    .then((i) => i.result)
}

export interface AIRecommendationArgs {
  tripId: any
  schema: AITripSchema
  regionCode: string
}

export async function getAIAccommodations(
  { tripId, schema, regionCode }: AIRecommendationArgs,
  accessToken?: string,
): Promise<definitions['aiRecommendation']> {
  const uri = `${baseUri}/ai/accommodation`
  return request
    .post<GetAIAccommodationResponse, GetAIAccommodationRequest>(
      uri,
      {
        tripId,
        userChoices: schema.trip,
        message: schema.message,
        region: regionCode,
        recommendedItems: schema.recommendedItems,
      },
      getOptions(accessToken),
    )
    .then((response) => response.result)
}

export async function getAIDestinations(
  { tripId, schema, regionCode }: AIRecommendationArgs,
  accessToken?: string,
): Promise<definitions['aiDestinationsResult']> {
  const uri = `${baseUri}/ai/destinations`
  return request
    .post<GetAIDestinationsResponse, GetAIDestinationsRequest>(
      uri,
      {
        tripId,
        userChoices: schema.trip,
        message: schema.message,
        region: regionCode,
        recommendedItems: schema.recommendedItems,
      },
      getOptions(accessToken),
    )
    .then((response) => response.result)
}

// to modify once BE implemented
export async function getAIAttractions(
  { tripId, schema, regionCode }: AIRecommendationArgs,
  accessToken?: string,
): Promise<definitions['aiRecommendation']> {
  const uri = `${baseUri}/ai/attractions`
  return request
    .post<GetAIAttractionsResponse, GetAIAttractionsRequest>(
      uri,
      {
        tripId,
        userChoices: schema.trip,
        message: schema.message,
        region: regionCode,
        recommendedItems: schema.recommendedItems,
      },
      getOptions(accessToken),
    )
    .then((response) => ({
      ...response.result,
      // Make sure there are no results without offerIds, until we support custom items
      recommendation: response.result.recommendation.filter((r) => !!r.offerId),
    }))
}

// to modify once BE implemented
export async function getAIDining(
  { tripId, schema, regionCode }: AIRecommendationArgs,
  accessToken?: string,
): Promise<definitions['aiRecommendation']> {
  const uri = `${baseUri}/ai/dining`
  return request
    .post<GetAIDiningResponse, GetAIDiningRequest>(
      uri,
      {
        tripId,
        userChoices: schema.trip,
        message: schema.message,
        region: regionCode,
        recommendedItems: schema.recommendedItems,
      },
      getOptions(accessToken),
    )
    .then((response) => ({
      ...response.result,
      // Make sure there are no results without offerIds, until we support custom items
      recommendation: response.result.recommendation.filter((r) => !!r.offerId),
    }))
}

export async function getAINotes(
  { tripId, schema, regionCode }: AIRecommendationArgs,
  accessToken?: string,
): Promise<definitions['aiNoteItem'][]> {
  const uri = `${baseUri}/ai/notes`
  return request
    .post<GetAINotesResponse, GetAINotesRequest>(
      uri,
      {
        tripId,
        userChoices: schema?.trip,
        message: schema?.message,
        region: regionCode,
      },
      getOptions(accessToken),
    )
    .then((response) => response.result)
}

export async function getLowStockItemsForTrip(
  {
    tripId,
  }: {
    tripId: string
  },
  accessToken?: string,
): Promise<definitions['lowStockItem'][]> {
  const uri = `${baseUri}/${tripId}/lowStockItems`

  return request
    .get<GetLowStockTripItemsResponse>(uri, getOptions(accessToken))
    .then((response) => response.result)
}
